import { useEffect, useState, useRef } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/useAuth";
import { useDictionary } from "../../context/useDictionary";
import { formatDate } from "../../services/utils";
import Autocomplete from "../common/Autocomplete";
import Table from "../common/Table";
import Modal from "../common/Modal";

export default function UserEdit({ userInit, index, list, setList, close, readOnlyFields }) {
  const [user, setUser] = useState({ ...userInit });
  const [roles, setRoles] = useState([]);
  const { checkPrivileges, employeeNames } = useAuth();
  const [listAutocomplete, setListAutocomplete] = useState([]);
  const [errors, setErrors] = useState({});

  const apiErrorRef = useRef(null);

  const { getDictionaryTypeValues } = useDictionary();
  const twOffices = getDictionaryTypeValues("TW Offices");

  useEffect(() => {
    if (twOffices.length > 0 && !user.visibilityRole.includes(-1) && user.visibilityRole.length === twOffices.length) {
      setValue("visibility", [-1]);
    }
    // eslint-disable-next-line
  }, [user.visibilityRole, twOffices]);

  useEffect(() => {
    setListAutocomplete(
      employeeNames.map((e) => ({
        value: e.hrCode,
        name: `${e.hrCode} - ${e.firstName} ${e.lastName}`,
      }))
    );
  }, [employeeNames]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const roles = await api.getRoles();
        if (roles.status === 200) {
          setRoles(roles.data);
        }
      } catch (error) {
        console.error("Error fetching roles", error);
      }
    };

    fetchRoles();
  }, []);

  const [actionLogs, setActionLogs] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [showActionLogModal, setShowActionLogModal] = useState(false);

  useEffect(() => {
    if (user.id) {
      setLoadingActions(true);
      api.getUserActionLogs(user.id).then((res) => {
        setActionLogs(Array.isArray(res.data) ? res.data : []);
        setLoadingActions(false);
      });
    }
  }, [user.id]);

  const handleEmployeeSelect = (item) => {
    if (!item.value) {
      setValue("_employee", null);
      setValue("firstName", "");
      setValue("lastName", "");
      setValue("email", "");
      return;
    }
    setValue("_employee", parseInt(item.value));

    const selectedEmp = employeeNames.find((e) => e.hrCode?.toString() === item.value?.toString());
    if (selectedEmp) {
      setValue("firstName", selectedEmp.firstName || "");
      setValue("lastName", selectedEmp.lastName || "");
      setValue("email", selectedEmp.email || "");
    }
  };

  const setValue = (key, value) => {
    setUser((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const newErrors = {};
    if (!user.firstName) newErrors.firstName = "First name is required";
    if (!user.lastName) newErrors.lastName = "Last name is required";
    if (!user.email) newErrors.email = "Email is required";
    if (!user._role) newErrors._role = "Role is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    let data;
    if (user.id) {
      data = await api.saveUser(user);
    } else {
      data = await api.createUser(user);
    }

    if (data.status !== 200) {
      const action = user.id ? "update" : "create";
      const errorMsg = data.data?.message || "An error occurred";
      setErrors((prev) => ({
        ...prev,
        api: `Can't ${action} user: ${errorMsg}`,
      }));
      setTimeout(() => {
        apiErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      return;
    }

    if (data.status === 200) {
      const index = list.findIndex((u) => u.id === user.id);
      if (index !== -1) {
        list[index] = data.data;
        setList([...list]);
      } else {
        list.push(data.data);
        setList([...list]);
      }
      close();
    }
  };

  const toggleVisibility = (officeId) => {
    if (user.visibilityRole.includes(-1)) {
      return;
    }
    const current = user.visibilityRole || [];
    if (current.includes(officeId)) {
      setValue(
        "visibilityRole",
        current.filter((id) => id !== officeId)
      );
    } else {
      setValue("visibilityRole", [...current, officeId]);
    }
  };

  const toggleSelectAllVisibility = () => {
    if (user.visibilityRole.includes(-1)) {
      setValue("visibilityRole", []);
    } else {
      setValue("visibilityRole", [-1]);
    }
  };

  const isProfileView = readOnlyFields && (!list || list.length === 0);

  const getListOffices = () => {
    if (user.visibilityRole.includes(-1)) return "All the Offices";
    const names = twOffices.filter((office) => user.visibilityRole.includes(office.id)).map((office) => office.value);
    return names.length > 0 ? names.join(", ") : "No office assigned";
  };

  return (
    <div className="modal-scroll">
      <div className="modal-header">{isProfileView ? "My Profile" : user.id ? "Edit User" : "Create User"}</div>
      <div className="modal-body modal-user-edit">
        <h2>User Info</h2>
        {errors.api && (
          <div className="error-message" style={{ marginBottom: 16 }} ref={apiErrorRef}>
            {errors.api}
          </div>
        )}
        <div>
          <div className="label-row">
            <label>Person</label>
            {readOnlyFields ? (
              <input
                value={
                  user._employee
                    ? listAutocomplete.find((e) => e.value === user._employee)?.name || user._employee
                    : "No person assigned"
                }
                readOnly={true}
              />
            ) : (
              <Autocomplete
                value={user._employee}
                items={listAutocomplete}
                onSelect={(item) => handleEmployeeSelect(item)}
                disabled={!!readOnlyFields}
              />
            )}
          </div>
        </div>
        {errors.email && <span className="error-message">{errors.email}</span>}
        <div className="label-row">
          <label>Email</label>
          <input value={user.email || ""} onChange={(e) => setValue("email", e.target.value)} readOnly={!!readOnlyFields} />
        </div>

        {errors.firstName && <span className="error-message">{errors.firstName}</span>}
        <div className="label-row">
          <label>First Name</label>
          <input
            value={user.firstName || ""}
            onChange={(e) => setValue("firstName", e.target.value)}
            readOnly={!!readOnlyFields}
          />
        </div>
        {errors.lastName && <span className="error-message">{errors.lastName}</span>}
        <div className="label-row">
          <label>Last Name</label>
          <input value={user.lastName || ""} onChange={(e) => setValue("lastName", e.target.value)} readOnly={!!readOnlyFields} />
        </div>
        <h2>Security</h2>
        {errors._role && <span className="error-message">{errors._role}</span>}
        <div className="label-row">
          <label>Role</label>
          {readOnlyFields ? (
            <input
              value={
                user._role
                  ? (roles.length > 0 ? roles.find((role) => role.id === user._role)?.name : null) ||
                    user.Role?.name ||
                    user._role
                  : "No role assigned"
              }
              readOnly={true}
            />
          ) : (
            <select value={user._role || ""} onChange={(e) => setValue("_role", e.target.value)} disabled={!!readOnlyFields}>
              <option value="" disabled>
                Select a role
              </option>
              {roles.map((role) => (
                <option key={`role_${role.id}`} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className={"label-row " + (!readOnlyFields ? "align-top mt-40" : "")}>
          <label>Visibility (TW Offices)</label>
          {readOnlyFields ? (
            <input value={getListOffices()} readOnly={true} />
          ) : (
            <div className="row col-2">
              <div className="input-group">
                <input
                  type="checkbox"
                  checked={!!user.visibilityRole && user.visibilityRole.includes(-1)}
                  onChange={() => toggleSelectAllVisibility()}
                  disabled={!!readOnlyFields}
                />
                All the Offices
              </div>
              {!user.visibilityRole.includes(-1) &&
                twOffices.map((office) => (
                  <div key={office.id} className="input-group">
                    <input
                      type="checkbox"
                      checked={!!user.visibilityRole && user.visibilityRole.includes(office.id)}
                      onChange={() => toggleVisibility(office.id)}
                      disabled={!!readOnlyFields}
                    />
                    {office.value}
                  </div>
                ))}
            </div>
          )}
        </div>
        <h2>Status</h2>
        <div className="label-row">
          <label>Archived</label>
          {readOnlyFields ? (
            <input value={user.isArchived ? "Archived" : "Active"} disabled />
          ) : (
            <input
              type="checkbox"
              checked={user.isArchived ?? false}
              onChange={(e) => setValue("isArchived", e.target.checked)}
            />
          )}
        </div>
      </div>
      {!readOnlyFields && (
        <div className="modal-footer">
          <div className="btn-bar-left">
            <div className="mt-10">
              <span>
                Created by {user.creator || "Admin"} {user.creationDate && `on ${formatDate(user.creationDate, true)}`}
              </span>
              {user.editor && user.editDate && (
                <span>
                  {" "}
                  - Edited by {user.editor} on {formatDate(user.editDate, true)}
                </span>
              )}
            </div>
          </div>
          <div className="btn-bar-right">
            {user.id && checkPrivileges("viewUserActionLogs") && (
              <button className="link" onClick={() => setShowActionLogModal(true)}>
                User Actions
              </button>
            )}
            <button className="link" onClick={close}>
              Cancel
            </button>
            <button className="blue" onClick={save}>
              {user.id ? "Update" : "Create User"}
            </button>
          </div>
        </div>
      )}

      {/* --- Action Log Modal --- */}
      {showActionLogModal && (
        <Modal close={() => setShowActionLogModal(false)} size="large">
          <div className="modal-header">User Actions</div>
          <div className="modal-body">
            <div className="table-container with-borders">
              <Table
                data={actionLogs}
                loading={loadingActions}
                config={{
                  columns: [
                    {
                      key: "action",
                      header: "Action",
                    },
                    {
                      key: "timestamp",
                      header: "Time",
                      template: (row) => (row.timestamp ? formatDate(row.timestamp, true) : "No Value"),
                    },
                    {
                      key: "details",
                      header: "Details",
                      template: (row) => {
                        const d = row.details || {};
                        if (!Object.keys(d).length) return "-";
                        return Object.entries(d)
                          .map(([key, value]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`)
                          .join(", ");
                      },
                    },
                  ],
                }}
                emptyMessage="No actions recorded."
                enableColumnVisibility={false}
                enableColumnOrdering={false}
                rowIcon="history"
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
